// src/components/RegulationsSidebar.jsx

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { BookMarked, FileCheck } from 'lucide-react';

const RegulationsSidebar = ({ laws, activeLawId, onLawSelect, isSearchBarVisible = true }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isSearchPage = location.pathname === '/search';

  const handleLawClick = (lawId) => {
    if (onLawSelect) {
      onLawSelect(lawId);
    }
    navigate(`/safety-regulations/${lawId}`);
  };

  const renderLawItem = (law) => {
    const isActive = activeLawId === law.id;
    return (
      <button
        key={law.id}
        onClick={() => handleLawClick(law.id)}
        className={`w-full text-left px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg transition-all duration-200 mb-1.5
          ${isActive 
            ? 'bg-green-50 text-green-900 font-semibold border border-green-200' 
            : 'text-gray-700 hover:bg-green-50 hover:text-green-700 border border-transparent'
          }`}
      >
        <span className="text-xs sm:text-sm font-medium break-words">{law.title}</span>
      </button>
    );
  };
  // 질의회시 — 기관별로 구분
  const menuItems = [
    {
      id: 'qa-chapters',
      title: '고용노동부 질의회시 (2022.5)',
      path: '/safety-regulations/qa',
      icon: <BookMarked size={18} className="text-blue-600" strokeWidth={2} />,
      subItems: [
        { id: 'ch1', title: '제1장 산업안전보건법 적용 일반', path: '/safety-regulations/qa/ch1/1-1/1', icon: <FileCheck size={16} className="text-green-600" strokeWidth={2} /> },
        { id: 'ch2', title: '제2장 안전보건관리체제 등', path: '/safety-regulations/qa/ch2/2-1/1', icon: <FileCheck size={16} className="text-green-600" strokeWidth={2} /> },
        { id: 'ch3', title: '제3장 안전보건교육', path: '/safety-regulations/qa/ch3/3-1/1', icon: <FileCheck size={16} className="text-green-600" strokeWidth={2} /> },
        { id: 'ch4', title: '제4장 유해･위험 방지 조치', path: '/safety-regulations/qa/ch4/4-1/1', icon: <FileCheck size={16} className="text-green-600" strokeWidth={2} /> },
        { id: 'ch5', title: '제5장 도급 시 산업재해 예방', path: '/safety-regulations/qa/ch5/5-1/1', icon: <FileCheck size={16} className="text-green-600" strokeWidth={2} /> },
        { id: 'ch6', title: '제6장 유해･위험 기계 등에 대한 조치', path: '/safety-regulations/qa/ch6/6-1/1', icon: <FileCheck size={16} className="text-green-600" strokeWidth={2} /> },
        { id: 'ch7', title: '제7장 유해･위험물질에 대한 조치', path: '/safety-regulations/qa/ch7/7-1/1', icon: <FileCheck size={16} className="text-green-600" strokeWidth={2} /> },
        { id: 'ch8', title: '제8장 근로자 보건관리', path: '/safety-regulations/qa/ch8/8-1/1', icon: <FileCheck size={16} className="text-green-600" strokeWidth={2} /> },
        { id: 'ch9', title: '제9장 근로자의 안전 및 보건', path: '/safety-regulations/qa/ch9/9-1/1', icon: <FileCheck size={16} className="text-green-600" strokeWidth={2} /> },
        { id: 'ch10', title: '제10장 벌칙', path: '/safety-regulations/qa/ch10/10-1/1', icon: <FileCheck size={16} className="text-green-600" strokeWidth={2} /> }
      ]
    },
    {
      id: 'qa-construction',
      title: '건설업 산업안전보건관리비 질의회시 (2025.6)',
      path: '/safety-regulations/qa-construction/1/1-1/1',
      icon: <FileCheck size={18} className="text-amber-600" strokeWidth={2} />,
      subItems: [
        { id: 'construction-intro', title: '산업안전보건관리비의 사용', path: '/safety-regulations/qa-construction/intro', icon: <FileCheck size={16} className="text-amber-600" strokeWidth={2} /> },
        { id: 'construction-ch1', title: '제1장 총칙 (정의 및 적용범위)', path: '/safety-regulations/qa-construction/1/1-1/1', icon: <FileCheck size={16} className="text-amber-600" strokeWidth={2} /> },
        { id: 'construction-ch2', title: '제2장 계상의무 및 기준', path: '/safety-regulations/qa-construction/1/1-1/5', icon: <FileCheck size={16} className="text-amber-600" strokeWidth={2} /> },
        { id: 'construction-ch3', title: '제3장 사용기준', path: '/safety-regulations/qa-construction/1/1-1/22', icon: <FileCheck size={16} className="text-amber-600" strokeWidth={2} /> },
        { id: 'construction-ch4', title: '제4장 사용금액의 감액·반환 등', path: '/safety-regulations/qa-construction/1/1-1/85', icon: <FileCheck size={16} className="text-amber-600" strokeWidth={2} /> },
        { id: 'construction-ch5', title: '제5장 사용내역서 작성 및 확인', path: '/safety-regulations/qa-construction/1/1-1/87', icon: <FileCheck size={16} className="text-amber-600" strokeWidth={2} /> },
        { id: 'construction-ch6', title: '제6장 안전관리자 제도 관련', path: '/safety-regulations/qa-construction/1/1-1/89', icon: <FileCheck size={16} className="text-amber-600" strokeWidth={2} /> }
      ]
    },
    {
      id: 'qa-heat',
      title: '폭염 관련 산업안전보건규칙 질의회시 (2026.5)',
      path: '/safety-regulations/qa-heat/ch1/1-1/1',
      icon: <FileCheck size={18} className="text-orange-600" strokeWidth={2} />,
      subItems: [
        { id: 'heat-ch1', title: '폭염작업 정의 및 해당 여부', path: '/safety-regulations/qa-heat/ch1/1-1/1', icon: <FileCheck size={16} className="text-orange-600" strokeWidth={2} /> },
        { id: 'heat-ch2', title: '체감온도 측정 및 기록·보관', path: '/safety-regulations/qa-heat/ch2/2-1/1', icon: <FileCheck size={16} className="text-orange-600" strokeWidth={2} /> },
        { id: 'heat-ch3', title: '휴식시간 부여', path: '/safety-regulations/qa-heat/ch3/3-1/1', icon: <FileCheck size={16} className="text-orange-600" strokeWidth={2} /> },
        { id: 'heat-ch4', title: '냉방장치·작업시간대 조정', path: '/safety-regulations/qa-heat/ch4/4-1/1', icon: <FileCheck size={16} className="text-orange-600" strokeWidth={2} /> },
        { id: 'heat-ch5', title: '폭염작업 장해 예방조치', path: '/safety-regulations/qa-heat/ch5/5-1/1', icon: <FileCheck size={16} className="text-orange-600" strokeWidth={2} /> },
        { id: 'heat-ch6', title: '기타사항', path: '/safety-regulations/qa-heat/ch6/6-1/1', icon: <FileCheck size={16} className="text-orange-600" strokeWidth={2} /> },
      ]
    },
    {
      id: 'qa-molit',
      title: '국토교통부 행정해석',
      path: '/safety-regulations/qa-molit',
      icon: <FileCheck size={18} className="text-sky-600" strokeWidth={2} />,
      subItems: [
        { id: 'molit-list', title: '행정해석 검색·목록', path: '/safety-regulations/qa-molit', icon: <FileCheck size={16} className="text-sky-600" strokeWidth={2} /> },
      ]
    },
    {
      id: 'qa-nfa',
      title: '소방청 행정해석',
      path: '/safety-regulations/qa-nfa',
      icon: <FileCheck size={18} className="text-rose-600" strokeWidth={2} />,
      subItems: [
        { id: 'nfa-list', title: '행정해석 검색·목록', path: '/safety-regulations/qa-nfa', icon: <FileCheck size={16} className="text-rose-600" strokeWidth={2} /> },
      ]
    }
  ];

  // 데이터 검증 및 안전하게 접근
  const safeMap = (items, mapFn) => {
    if (!items || !Array.isArray(items)) return null;
    return items.map(mapFn);
  };

  return (
    <div
      className="w-80 bg-white border-r border-gray-200 overflow-y-auto transition-all duration-500 ease-in-out"
      style={{
        marginTop: isSearchPage ? '60px' : (isSearchBarVisible ? '130px' : '80px')
      }}
    >
      <div className={`${isSearchPage ? 'pt-2' : 'pt-4'} pb-4 px-2 sm:px-4`}>
        {/* 안전법규 */}
        <h3 className="text-base sm:text-lg font-bold mb-3 sm:mb-4 text-gray-900 px-2 sm:px-0">안전법규(최신개정일)</h3>
        <nav className="mb-6 sm:mb-8">
          {safeMap(
            laws && Array.isArray(laws)
              ? [
                  ...laws.filter(law => law.title === '산업안전보건법' || law.id === '001766'),
                  ...laws.filter(law => law.title === '산업안전보건법 시행령' || law.id === '003786'),
                  ...laws.filter(law => law.title === '산업안전보건법 시행규칙' || law.id === '007364'),
                  ...laws.filter(law => law.title === '산업안전보건기준에 관한 규칙' || law.id === '007363'),
                  ...laws.filter(law => 
                    law.title === '유해·위험작업의 취업 제한에 관한 규칙' || 
                    law.title === '유해ㆍ위험작업의 취업 제한에 관한 규칙' ||
                    law.id === '007844'
                  ),
                  ...laws.filter(law => law.title === '중대재해 처벌 등에 관한 법률' || law.id === '013993'),
                  ...laws.filter(law => law.title === '중대재해 처벌 등에 관한 법률 시행령' || law.id === '014159'),
                  ...laws.filter(law => law.title === '건설기술 진흥법' || law.id === '001807'),
                  ...laws.filter(law => law.title === '건설기술 진흥법 시행령' || law.id === '002111'),
                  ...laws.filter(law => law.title === '건설기술 진흥법 시행규칙' || law.id === '006175')
                ]
              : laws,
            renderLawItem
          )}
        </nav>

        {/* 질의회시 · 행정해석 */}
        <button
          type="button"
          onClick={() => navigate('/safety-regulations/qa')}
          className="w-full text-left text-base sm:text-lg font-bold mb-3 sm:mb-4 text-gray-900 px-2 sm:px-0 hover:text-green-800 transition-colors"
        >
          질의회시 · 행정해석
        </button>
        <p className="text-xs text-gray-500 px-2 sm:px-0 mb-3 -mt-2">
          기관을 선택한 뒤 자료를 열람합니다.
        </p>
        {menuItems.map((item) => (
          <div key={item.id} className="mb-4 sm:mb-6">
            <h4 className="text-sm sm:text-base font-semibold mb-2 sm:mb-3 text-gray-800 px-2 sm:px-0 flex items-center gap-2">
              {item.icon}
              {item.title}
            </h4>
            <nav>
              {safeMap(item.subItems, (subItem) => {
                const isActive = location.pathname === subItem.path
                  || (item.id === 'qa-molit' && location.pathname.startsWith('/safety-regulations/qa-molit'))
                  || (item.id === 'qa-nfa' && location.pathname.startsWith('/safety-regulations/qa-nfa'));
                return (
                <a
                  key={subItem.id}
                  href={subItem.path}
                  className={`group flex items-center gap-2 sm:gap-2.5 p-2 sm:p-2.5 rounded-lg transition-all duration-200 hover:shadow-sm mb-1 ${
                    isActive
                      ? 'bg-green-50 text-green-800 border border-green-200'
                      : 'text-gray-700 hover:bg-green-50 hover:text-green-700 border border-transparent'
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(subItem.path);
                  }}
                >
                  <span className="group-hover:scale-110 transition-transform duration-200 flex-shrink-0">
                    {subItem.icon}
                  </span>
                  <span className="text-xs sm:text-sm font-medium break-words">{subItem.title}</span>
                </a>
                );
              })}
            </nav>
          </div>
        ))}

      </div>
    </div>
  );
};

export default RegulationsSidebar;
